import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, RefreshCw,
  Loader2, X, Clock, Trash2, Edit2, CheckCircle2, DollarSign, Search, Cake, UserPlus, Check, AlertCircle,
  CalendarDays, BellRing, Phone, RotateCcw
} from 'lucide-react';
import { auth } from '../config/firebase';
import { agendaService, clienteService, servicoService } from '../services/api';
import EnviarLembreteRapido from './EnviarLembreteRapido';

const formatarPreco = (preco) => {
  if (preco === null || preco === undefined || preco === '') return null;
  return Number(preco).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Aceita telefone com ou sem prefixo 55 (o dado é inconsistente entre os canais que criam
// clientes — bot/link público prefixam 55, cadastro manual não) e devolve só os dígitos locais.
const digitosLocais = (raw) => {
  let value = (raw || '').replace(/\D/g, '');
  if (value.length > 11 && value.startsWith('55')) value = value.slice(2);
  return value.slice(0, 11);
};

const formatarTelefoneExibicao = (raw) => {
  let value = digitosLocais(raw);
  if (value.length > 2) value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
  if (value.length > 10) value = `${value.slice(0, 10)}-${value.slice(10)}`;
  return value;
};

const VisualizarConsultas = ({ isPremium, getLimite }) => {
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  // Consulta cujo cliente vai receber um lembrete (abre o modal com CriarLembreteAvulso)
  const [consultaLembrete, setConsultaLembrete] = useState(null);
  // Data (YYYY-MM-DD) pra rolar a lista até a seção certa depois de um salto pelo topo
  const [scrollAlvo, setScrollAlvo] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingConsultaId, setEditingConsultaId] = useState(null);
  const [googleEventId, setGoogleEventId] = useState(null);

  const [formData, setFormData] = useState({
    pacienteNome: '', pacienteTelefone: '', data: '', horario: '', observacao: '', dataNascimento: ''
  });

  const [clientes, setClientes] = useState([]);
  const [buscaCliente, setBuscaCliente] = useState('');
  const [modoNovoCliente, setModoNovoCliente] = useState(false);

  const [servicos, setServicos] = useState([]);
  const [servicosIds, setServicosIds] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [buscandoHorarios, setBuscandoHorarios] = useState(false);

  const hojeInput = new Date().toLocaleDateString('en-CA');

  const carregarClientes = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await clienteService.listar(token);
      setClientes(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const carregarServicos = async () => {
    try {
      const res = await servicoService.listar(auth.currentUser.uid);
      setServicos(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar serviços:', error);
    }
  };

  useEffect(() => { carregarClientes(); carregarServicos(); }, []);

  const alternarServico = (id) => {
    setServicosIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const servicosSelecionados = servicos.filter(s => servicosIds.includes(s.id));
  const totalDuracao = servicosSelecionados.reduce((soma, s) => soma + (s.duracao_minutos || 0), 0);
  const totalPreco = servicosSelecionados.reduce((soma, s) => soma + (s.preco ? Number(s.preco) : 0), 0);

  const buscarHorariosParaAgendamento = useCallback(async () => {
    if (editingConsultaId || servicosIds.length === 0 || !formData.data) return;
    setBuscandoHorarios(true);
    setHorarios([]);
    setFormData(f => ({ ...f, horario: '' }));
    try {
      const res = await agendaService.buscarHorarios(auth.currentUser.uid, servicosIds, formData.data);
      setHorarios(res.data || []);
    } catch (error) {
      setHorarios([]);
    } finally {
      setBuscandoHorarios(false);
    }
  }, [editingConsultaId, servicosIds, formData.data]);

  useEffect(() => { buscarHorariosParaAgendamento(); }, [buscarHorariosParaAgendamento]);

  const buscaNormalizada = buscaCliente.trim().toLowerCase();
  const buscaDigitos = buscaCliente.replace(/\D/g, '');
  const clientesFiltrados = clientes.filter(cliente => {
    if (!buscaNormalizada) return false;
    const nomeCombina = cliente.nome?.toLowerCase().includes(buscaNormalizada);
    const telefoneCombina = buscaDigitos && cliente.telefone?.includes(buscaDigitos);
    return nomeCombina || telefoneCombina;
  });

  const selecionarCliente = (cliente) => {
    setFormData(f => ({ ...f, pacienteNome: cliente.nome, pacienteTelefone: digitosLocais(cliente.telefone) }));
    setBuscaCliente('');
  };

  const iniciarNovoCliente = () => {
    setModoNovoCliente(true);
    setFormData(f => ({ ...f, pacienteNome: buscaCliente.trim(), pacienteTelefone: '', dataNascimento: '' }));
    setBuscaCliente('');
  };

  const limparClienteSelecionado = () => {
    setModoNovoCliente(false);
    setFormData(f => ({ ...f, pacienteNome: '', pacienteTelefone: '', dataNascimento: '' }));
  };

  const carregarConsultas = async (silent = false) => {
    if (!silent) setLoading(true);
    else setIsRefreshing(true);
    try {
      const user = auth.currentUser;
      if (user) {
        const response = await agendaService.listarMinhasConsultas(user.uid);
        setConsultas(response.data || []);
      }
    } catch (error) {
      console.error("Erro ao carregar:", error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => { carregarConsultas(); }, []);

  const mudarMes = (offset) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1));
  };

  const irParaHoje = () => {
    const hoje = new Date();
    setCurrentDate(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
    setScrollAlvo(hojeInput);
  };

  // Salto direto pra qualquer data pelo topo: muda o mês exibido e rola até a seção do dia
  const irParaData = (dataISO) => {
    if (!dataISO) return;
    const [ano, mes] = dataISO.split('-').map(Number);
    setCurrentDate(new Date(ano, mes - 1, 1));
    setScrollAlvo(dataISO);
  };

  useEffect(() => {
    if (!scrollAlvo) return;
    const el = document.getElementById(`dia-${scrollAlvo}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setScrollAlvo(null);
  }, [scrollAlvo, consultas, currentDate]);

  // Agrupa as consultas do mês exibido por dia (só dias que têm algo), ordenando dias e
  // horários — é a estrutura que a lista renderiza no lugar do antigo grid de calendário.
  const mesPrefixo = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
  const gruposDoMes = (() => {
    const porDia = {};
    consultas
      .filter(c => c.data_inicio.startsWith(mesPrefixo))
      .forEach(c => {
        const dataISO = c.data_inicio.slice(0, 10);
        if (!porDia[dataISO]) porDia[dataISO] = [];
        porDia[dataISO].push(c);
      });
    return Object.keys(porDia)
      .sort()
      .map(dataISO => ({
        dataISO,
        itens: porDia[dataISO].sort((a, b) => a.data_inicio.localeCompare(b.data_inicio))
      }));
  })();

  const formatarTituloDia = (dataISO) => {
    const d = new Date(`${dataISO}T12:00:00`);
    const rotulo = d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
    const amanha = new Date();
    amanha.setDate(amanha.getDate() + 1);
    if (dataISO === hojeInput) return { destaque: 'Hoje', rotulo };
    if (dataISO === amanha.toLocaleDateString('en-CA')) return { destaque: 'Amanhã', rotulo };
    return { destaque: null, rotulo };
  };

  const abrirModalCriar = () => {
    setEditingConsultaId(null);
    setGoogleEventId(null);
    setFormData({ pacienteNome: '', pacienteTelefone: '', data: hojeInput, horario: '', observacao: '', dataNascimento: '' });
    setBuscaCliente('');
    setModoNovoCliente(false);
    setServicosIds([]);
    setHorarios([]);
    setIsModalOpen(true);
  };

  const abrirModalEditar = (consulta) => {
    setEditingConsultaId(consulta.id);
    setGoogleEventId(consulta.google_event_id);

    const dataLocal = new Date(consulta.data_inicio);

    const ano = dataLocal.getFullYear();
    const mes = String(dataLocal.getMonth() + 1).padStart(2, '0');
    const dia = String(dataLocal.getDate()).padStart(2, '0');
    const horas = String(dataLocal.getHours()).padStart(2, '0');
    const minutos = String(dataLocal.getMinutes()).padStart(2, '0');

    const dataFormatada = `${ano}-${mes}-${dia}`;
    const horarioFormatado = `${horas}:${minutos}`;

    let telefoneExibicao = consulta.paciente_telefone || '';
    if (telefoneExibicao.startsWith('55')) {
      telefoneExibicao = telefoneExibicao.substring(2);
    }

    setFormData({
      pacienteNome: consulta.paciente_nome,
      pacienteTelefone: telefoneExibicao,
      data: dataFormatada,
      horario: horarioFormatado,
      observacao: consulta.observacao || '',
      dataNascimento: ''
    });
    setBuscaCliente('');
    setModoNovoCliente(false);
    setServicosIds([]);
    setHorarios([]);
    setIsModalOpen(true);
  };

  const handleAgendar = async (e) => {
    e.preventDefault();

    if (!editingConsultaId && !formData.pacienteNome) {
      return alert("❌ Busque um cliente já cadastrado ou cadastre um novo antes de agendar.");
    }
    if (!editingConsultaId && servicosIds.length === 0) {
      return alert("❌ Escolha ao menos um serviço antes de agendar.");
    }

    const agora = new Date();
    const dataInicio = new Date(`${formData.data}T${formData.horario}:00`);

    if (dataInicio < agora) {
      return alert("❌ Você não pode agendar ou alterar um atendimento para um horário que já passou!");
    }

    // Mesmo padrão de normalização usado no restante do app (PerfilTab.js, AgendaPublica.js):
    // só dígitos, prefixado com 55. Precisa bater exatamente com o telefone já salvo em
    // `clientes` pra buscarOuCriarCliente vincular ao cadastro certo em vez de duplicar.
    let tel = (formData.pacienteTelefone || '').replace(/\D/g, '');
    if (tel.length === 10 || tel.length === 11) tel = `55${tel}`;
    const telFinal = tel;

    // No agendamento manual a duração vem da soma dos serviços escolhidos (igual ao link
    // público); na edição não há seleção de serviço, então mantém os 30min padrão de sempre.
    const duracaoMinutos = !editingConsultaId && totalDuracao > 0 ? totalDuracao : 30;

    setIsSubmitting(true);
    try {
      const payload = {
        pacienteNome: formData.pacienteNome,
        pacienteTelefone: telFinal,
        dataInicio: dataInicio.toISOString(),
        dataFim: new Date(dataInicio.getTime() + duracaoMinutos * 60000).toISOString(),
        observacao: formData.observacao,
        ...(modoNovoCliente && formData.dataNascimento ? { data_nascimento: formData.dataNascimento } : {}),
        ...(!editingConsultaId ? { servicos_ids: servicosIds } : {})
      };

      if (editingConsultaId) {
        await agendaService.editar(auth.currentUser.uid, {
          consultaId: editingConsultaId,
          googleEventId: googleEventId,
          ...payload
        });
        alert("✨ Atendimento atualizado com sucesso!");
      } else {
        await agendaService.agendar(auth.currentUser.uid, { ...payload });
        alert("✨ Atendimento criado com sucesso!");
      }

      setIsModalOpen(false);
      setFormData({ pacienteNome: '', pacienteTelefone: '', data: '', horario: '', observacao: '', dataNascimento: '' });
      setBuscaCliente('');
      setModoNovoCliente(false);
      setServicosIds([]);
      setHorarios([]);
      carregarConsultas(true);
      if (!editingConsultaId) carregarClientes();
    } catch (err) {
      if (err.response?.data?.erro) {
        alert(`❌ ${err.response.data.erro}`);
      } else if (err.response && err.response.status === 409) {
        alert("❌ Esse horário já está ocupado por outro atendimento.");
      } else {
        alert("Erro ao salvar o agendamento.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelar = async (id, gId) => {
    if (!window.confirm("Confirmar cancelamento?")) return;
    try {
      await agendaService.cancelar(auth.currentUser.uid, { consultaId: id, googleEventId: gId });
      carregarConsultas(true);
    } catch (e) { alert("Erro ao cancelar."); }
  };

  const handleConcluir = async (id) => {
    try {
      await agendaService.concluir(auth.currentUser.uid, { consultaId: id });
      carregarConsultas(true);
    } catch (e) { alert("Erro ao marcar como concluído."); }
  };

  const handleReverterConclusao = async (id) => {
    if (!window.confirm("Reverter a confirmação de comparecimento? O atendimento volta a ficar em aberto (editável e cancelável).")) return;
    try {
      await agendaService.reverterConclusao(auth.currentUser.uid, { consultaId: id });
      carregarConsultas(true);
    } catch (e) { alert("Erro ao reverter a confirmação."); }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] w-full gap-4">
        <Loader2 className="animate-spin text-[#1883FF]" size={40} />
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Sincronizando agenda...</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-2 sm:p-4">

      {/* CABEÇALHO DO CALENDÁRIO */}
      {!isPremium && (
        <div className="mb-4 flex items-center justify-center sm:justify-start">
          <span className="text-[10px] font-black uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-100 px-3 py-1.5 rounded-lg">
            Plano Gratuito: {consultas.filter(c => c.data_inicio.startsWith(hojeInput) && c.status !== 'cancelado').length}/{getLimite('agendamentos_dia')} agendamentos hoje
          </span>
        </div>
      )}
      {/* TOPO DE NAVEGAÇÃO RÁPIDA: em 3 blocos que empilham no mobile (feito pra parecer um
          cabeçalho de app — nav de mês, ações rápidas, CTA de destaque) e viram uma única linha
          a partir de lg:, reaproveitando o espaço extra de tela. */}
      <div className="sticky top-0 z-40 bg-[#F8F7FF]/95 backdrop-blur-sm -mx-2 sm:-mx-4 px-2 sm:px-4 py-3 mb-6 border-b border-purple-50">
        <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between lg:gap-3">

          {/* Bloco 1: navegação de mês */}
          <div className="flex items-center gap-2 lg:flex-none">
            <button className="shrink-0 bg-white border border-slate-200 p-3 lg:p-2.5 rounded-xl cursor-pointer hover:bg-[#F8F7FF] hover:border-purple-100 active:scale-95 transition-all text-slate-600" onClick={() => mudarMes(-1)} title="Mês anterior">
              <ChevronLeft size={18}/>
            </button>
            <h2 className="flex-1 lg:flex-none text-sm sm:text-base lg:text-lg font-black uppercase italic text-slate-900 text-center lg:min-w-[150px] m-0 truncate">
              {currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
            </h2>
            <button className="shrink-0 bg-white border border-slate-200 p-3 lg:p-2.5 rounded-xl cursor-pointer hover:bg-[#F8F7FF] hover:border-purple-100 active:scale-95 transition-all text-slate-600" onClick={() => mudarMes(1)} title="Próximo mês">
              <ChevronRight size={18}/>
            </button>
          </div>

          {/* Bloco 2: ações rápidas — Hoje e salto direto por data */}
          <div className="flex items-center gap-2 lg:flex-none">
            <button className="flex-1 lg:flex-none bg-white border border-purple-100 text-[#6D28D9] px-4 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-wider cursor-pointer hover:bg-[#F5F3FF] active:scale-95 transition-all" onClick={irParaHoje}>
              Hoje
            </button>
            <div className="flex-1 lg:flex-none flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3" title="Ir direto pra uma data">
              <CalendarDays size={14} className="text-slate-400 shrink-0" />
              <input
                type="date"
                className="w-full lg:w-auto py-2.5 bg-transparent border-none outline-none text-xs font-bold text-slate-700 cursor-pointer"
                onChange={(e) => irParaData(e.target.value)}
              />
            </div>
          </div>

          {/* Bloco 3: CTA de destaque — Agendar (largura cheia no mobile, como um botão de app) + atualizar */}
          <div className="flex items-center gap-2.5 lg:flex-none">
            <button className="flex-1 lg:flex-none bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none px-5 py-3.5 lg:py-3 rounded-xl text-[11px] font-black tracking-wider uppercase cursor-pointer flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(109,40,217,0.2)] hover:opacity-90 hover:-translate-y-0.5 active:scale-95 transition-all" onClick={abrirModalCriar}>
              <Plus size={16}/> AGENDAR
            </button>
            <button className="shrink-0 bg-white border border-slate-200 p-3.5 lg:p-3 rounded-xl cursor-pointer hover:bg-[#F8F7FF] hover:border-purple-100 active:scale-95 transition-all text-slate-600" onClick={() => carregarConsultas(true)} title="Atualizar">
              <RefreshCw size={16} className={isRefreshing ? 'animate-spin text-[#1883FF]' : ''}/>
            </button>
          </div>
        </div>
      </div>

      {/* LISTA DE AGENDAMENTOS AGRUPADA POR DATA */}
      {gruposDoMes.length === 0 ? (
        <div className="bg-[#F8F7FF] border border-dashed border-purple-100 rounded-[24px] p-12 flex flex-col items-center justify-center text-center">
          <CalendarDays size={40} className="text-slate-300 mb-4" />
          <p className="text-sm font-black text-slate-600 m-0 mb-1">Nenhum atendimento neste mês.</p>
          <p className="text-xs text-slate-400 font-medium m-0">Use as setas acima pra navegar entre os meses ou clique em AGENDAR.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          {gruposDoMes.map(({ dataISO, itens }) => {
            const { destaque, rotulo } = formatarTituloDia(dataISO);
            const ativos = itens.filter(c => c.status !== 'cancelado').length;
            return (
              <section key={dataISO} id={`dia-${dataISO}`} className="scroll-mt-24">
                {/* Cabeçalho da data */}
                <div className={`flex items-center gap-3 mb-3 pb-2 border-b-2 ${dataISO === hojeInput ? 'border-[#6D28D9]' : 'border-slate-100'}`}>
                  {destaque && (
                    <span className="bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-lg shadow-sm shadow-[#6D28D9]/20">
                      {destaque}
                    </span>
                  )}
                  <h3 className="text-sm font-black uppercase text-slate-800 m-0 capitalize">{rotulo}</h3>
                  <span className="text-[10px] font-bold text-slate-400 ml-auto">
                    {ativos} atendimento{ativos === 1 ? '' : 's'}
                  </span>
                </div>

                {/* Cards do dia */}
                <div className="flex flex-col gap-3">
                  {itens.map((c) => {
                    const isCancelado = c.status === 'cancelado';
                    const isConcluido = c.status === 'concluido';

                    // Ações disponíveis pra esse card — mesma lista usada na barra mobile (em
                    // grade, ícone+legenda) e na fileira compacta de desktop. Concluído deixa de
                    // travar Alterar/Cancelar: o gestor pode reeditar ou reverter mesmo depois de
                    // confirmar o comparecimento (só perde o botão "Confirmar", que vira "Reverter").
                    const acoes = isCancelado ? [] : [
                      isConcluido
                        ? { key: 'reverter', label: 'Reverter', icon: RotateCcw, onClick: () => handleReverterConclusao(c.id), title: 'Desfazer confirmação de comparecimento', mobileClass: 'bg-amber-50 text-amber-600', desktopClass: 'bg-amber-50 text-amber-600 border border-amber-100 hover:bg-amber-100', desktopLabel: true }
                        : { key: 'confirmar', label: 'Confirmar', icon: CheckCircle2, onClick: () => handleConcluir(c.id), title: 'Confirmar que o atendimento foi realizado', mobileClass: 'bg-emerald-50 text-emerald-600', desktopClass: 'bg-emerald-50 text-emerald-600 border border-emerald-100 hover:bg-emerald-100', desktopLabel: true },
                      { key: 'editar', label: 'Alterar', icon: Edit2, onClick: () => abrirModalEditar(c), title: 'Alterar data/horário/dados', mobileClass: 'bg-[#F5F3FF] text-[#6D28D9]', desktopClass: 'bg-[#F5F3FF] text-[#6D28D9] border border-purple-100 hover:bg-[#EDE9FE]', desktopLabel: false },
                      { key: 'cancelar', label: 'Cancelar', icon: Trash2, onClick: () => handleCancelar(c.id, c.google_event_id), title: 'Cancelar atendimento', mobileClass: 'bg-red-50 text-red-500', desktopClass: 'bg-red-50 text-red-500 border border-red-100 hover:bg-red-100', desktopLabel: false },
                      { key: 'lembrete', label: 'Lembrete', icon: BellRing, onClick: () => setConsultaLembrete(c), title: 'Enviar lembrete salvo ou personalizado', mobileClass: 'bg-gradient-to-br from-[#1883FF] to-[#6D28D9] text-white', desktopClass: 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none hover:opacity-90 shadow-sm shadow-[#6D28D9]/15', desktopLabel: true },
                    ];

                    return (
                      <div key={c.id} className={`p-4 sm:p-5 rounded-2xl border transition-all ${isCancelado ? 'bg-slate-50/60 border-dashed border-slate-200 opacity-75' : isConcluido ? 'bg-emerald-50/50 border-emerald-100' : 'bg-[#F8F7FF] border-purple-50'}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">

                          {/* Horário */}
                          <div className={`shrink-0 flex sm:flex-col items-center gap-1.5 sm:gap-0 sm:w-[68px] sm:text-center ${isCancelado ? 'text-slate-400' : isConcluido ? 'text-emerald-700' : 'text-[#6D28D9]'}`}>
                            <Clock size={13} className="sm:mb-0.5" />
                            <span className="text-base font-black tracking-tight">
                              {new Date(c.data_inicio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>

                          {/* Cliente + detalhes */}
                          <div className="flex-1 min-w-0 sm:border-l sm:border-slate-100 sm:pl-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className={`text-sm font-black text-slate-800 m-0 truncate ${isCancelado ? 'line-through text-slate-400 font-medium' : ''}`}>
                                {c.paciente_nome}
                              </h4>
                              {isCancelado && (
                                <span className="bg-red-50 text-red-600 text-[9px] px-2 py-0.5 rounded-md font-black tracking-wide">CANCELADO</span>
                              )}
                              {isConcluido && (
                                <span className="bg-emerald-100 text-emerald-700 text-[9px] px-2 py-0.5 rounded-md font-black tracking-wide">CONCLUÍDO</span>
                              )}
                              {isConcluido && formatarPreco(c.valor_final) && (
                                <span className="text-xs font-black text-emerald-700 flex items-center gap-0.5"><DollarSign size={11} />{formatarPreco(c.valor_final)}</span>
                              )}
                            </div>
                            {c.paciente_telefone && (
                              <p className="m-0 mt-1 text-[11px] font-bold text-slate-400 flex items-center gap-1.5">
                                <Phone size={10} /> {formatarTelefoneExibicao(c.paciente_telefone)}
                              </p>
                            )}
                            {!isCancelado && c.observacao && (
                              <p className="m-0 mt-1.5 text-xs text-slate-500 italic truncate">{c.observacao}</p>
                            )}
                            {isConcluido && !c.lembrete_enviado && (
                              <p className="m-0 mt-1.5 text-[10px] font-black text-amber-700 bg-amber-50 px-2 py-1 rounded-lg flex items-center gap-1.5 w-fit">
                                <AlertCircle size={12} /> Lembrete não enviado a este paciente
                              </p>
                            )}
                          </div>

                          {/* Ações (desktop): fileira compacta à direita, ícone-only pra Alterar/Cancelar */}
                          {acoes.length > 0 && (
                            <div className="hidden sm:flex items-center gap-1.5 shrink-0 justify-end">
                              {acoes.map(a => (
                                <button
                                  key={a.key}
                                  onClick={a.onClick}
                                  title={a.title}
                                  className={`flex items-center justify-center gap-1.5 rounded-xl cursor-pointer transition-colors text-[10px] font-black uppercase tracking-wide ${a.desktopLabel ? 'px-3 py-2' : 'p-2'} ${a.desktopClass}`}
                                >
                                  <a.icon size={a.desktopLabel ? 13 : 14}/> {a.desktopLabel && a.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Ações (mobile): barra de ícone+legenda em grade, como um app nativo —
                            substitui a fileira apertada de ícones que ficava difícil de tocar. */}
                        {acoes.length > 0 && (
                          <div className={`sm:hidden mt-3 pt-3 border-t border-slate-100 grid gap-1.5`} style={{ gridTemplateColumns: `repeat(${acoes.length}, minmax(0, 1fr))` }}>
                            {acoes.map(a => (
                              <button
                                key={a.key}
                                onClick={a.onClick}
                                className={`flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-wide cursor-pointer transition-transform active:scale-95 ${a.mobileClass}`}
                              >
                                <a.icon size={17}/>
                                {a.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* MODAL DE LEMBRETE: envio imediato, escolhendo um modelo salvo ou digitando na hora */}
      {consultaLembrete && (
        <div className="fixed inset-0 bg-slate-900/60 z-[1200] backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-[480px] rounded-[32px] p-6 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center mb-6 shrink-0">
              <h3 className="font-black text-slate-900 uppercase tracking-wide text-sm m-0 flex items-center gap-2">
                <BellRing size={16} className="text-[#1883FF]" /> Enviar Lembrete
              </h3>
              <button className="bg-slate-50 border-none p-1.5 rounded-lg text-slate-400 cursor-pointer hover:bg-[#F5F3FF] hover:text-[#6D28D9] transition-colors" onClick={() => setConsultaLembrete(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto pr-1">
              <EnviarLembreteRapido consulta={consultaLembrete} onEnviado={() => setConsultaLembrete(null)} />
            </div>
          </div>
        </div>
      )}

      {/* MODAL CRIAR / EDITAR AGENDAMENTO */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[1200] backdrop-blur-xs flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white w-full max-w-[450px] rounded-[24px] sm:rounded-[32px] p-5 sm:p-8 shadow-2xl border border-slate-100 animate-scale-in flex flex-col max-h-[95vh] sm:max-h-[90vh]">

            <div className="flex justify-between items-center mb-4 sm:mb-6 shrink-0">
              <h3 className="font-black text-slate-900 uppercase tracking-wide text-base m-0">
                {editingConsultaId ? "Editar Atendimento" : "Novo Agendamento"}
              </h3>
              <button className="bg-slate-50 border-none p-1.5 rounded-lg text-slate-400 cursor-pointer hover:bg-[#F5F3FF] hover:text-[#6D28D9]" onClick={() => setIsModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAgendar} className="flex-1 min-h-0 overflow-y-auto pr-1 flex flex-col gap-4">
              {!editingConsultaId ? (
                <div className="flex flex-col gap-1.5">
                  {modoNovoCliente ? (
                    <>
                      <input
                        required
                        type="text"
                        className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-medium text-slate-800 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                        placeholder="Nome do Paciente"
                        value={formData.pacienteNome}
                        onChange={e => setFormData({ ...formData, pacienteNome: e.target.value })}
                      />
                      <input
                        type="text"
                        className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-medium text-slate-800 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                        placeholder="WhatsApp (ex: 85988887766) - Opcional"
                        value={formData.pacienteTelefone}
                        onChange={e => setFormData({ ...formData, pacienteTelefone: e.target.value })}
                      />
                      <div className="flex items-center gap-3 bg-[#F8F7FF] border border-purple-50 rounded-xl px-4">
                        <Cake size={18} className="text-slate-400 shrink-0" />
                        <input
                          type="date"
                          max={hojeInput}
                          value={formData.dataNascimento}
                          onChange={e => setFormData({ ...formData, dataNascimento: e.target.value })}
                          className="w-full py-3.5 bg-transparent border-none outline-none text-sm font-medium text-slate-800"
                        />
                      </div>
                      <p className="text-[10px] text-slate-400 font-semibold ml-1 m-0">Data de nascimento é opcional.</p>
                      <button
                        type="button"
                        onClick={limparClienteSelecionado}
                        className="self-start text-[10px] font-bold text-[#6D28D9] bg-transparent border-none cursor-pointer underline mt-1"
                      >
                        ← Buscar cliente já cadastrado
                      </button>
                    </>
                  ) : formData.pacienteNome ? (
                    <div className="flex items-center justify-between gap-3 bg-[#F5F3FF] border border-purple-100 rounded-xl px-4 py-3">
                      <div className="min-w-0">
                        <p className="m-0 text-sm font-bold text-slate-800 truncate">{formData.pacienteNome}</p>
                        <p className="m-0 text-xs text-slate-400 font-semibold truncate">{formatarTelefoneExibicao(formData.pacienteTelefone)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={limparClienteSelecionado}
                        className="shrink-0 text-[10px] font-black uppercase text-[#6D28D9] bg-white border border-purple-100 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#EDE9FE] transition-colors"
                      >
                        Trocar
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 bg-[#F8F7FF] border border-purple-50 rounded-xl px-4">
                        <Search size={18} className="text-slate-400 shrink-0" />
                        <input
                          type="text"
                          placeholder="Buscar cliente por nome ou telefone"
                          value={buscaCliente}
                          onChange={e => setBuscaCliente(e.target.value)}
                          className="w-full py-3.5 bg-transparent border-none outline-none text-sm font-medium text-slate-800"
                        />
                      </div>
                      {buscaCliente.trim() && (
                        <div className="flex flex-col border border-purple-50 rounded-xl bg-white shadow-sm max-h-[180px] overflow-y-auto">
                          {clientesFiltrados.length === 0 ? (
                            <p className="text-xs text-slate-400 italic px-4 py-3 m-0">Nenhum cliente encontrado.</p>
                          ) : (
                            clientesFiltrados.slice(0, 6).map(cliente => (
                              <button
                                type="button"
                                key={cliente.id}
                                onClick={() => selecionarCliente(cliente)}
                                className="text-left px-4 py-2.5 bg-transparent border-none border-b border-slate-50 last:border-0 cursor-pointer hover:bg-[#F5F3FF] transition-colors"
                              >
                                <p className="m-0 text-sm font-bold text-slate-800">{cliente.nome}</p>
                                <p className="m-0 text-xs text-slate-400 font-semibold">{formatarTelefoneExibicao(cliente.telefone)}</p>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={iniciarNovoCliente}
                        className="self-start flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide text-[#6D28D9] bg-[#F5F3FF] border-none px-3 py-2 rounded-lg cursor-pointer hover:bg-[#EDE9FE] transition-colors mt-1"
                      >
                        <UserPlus size={12} /> Cadastrar novo cliente
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-1.5">
                    <input
                      required
                      type="text"
                      className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-medium text-slate-800 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                      placeholder="Nome do Paciente"
                      value={formData.pacienteNome}
                      onChange={e => setFormData({ ...formData, pacienteNome: e.target.value })}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <input
                      type="text"
                      className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-medium text-slate-800 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                      placeholder="WhatsApp (ex: 85988887766) - Opcional"
                      value={formData.pacienteTelefone}
                      onChange={e => setFormData({ ...formData, pacienteTelefone: e.target.value })}
                    />
                  </div>
                </>
              )}

              {!editingConsultaId ? (
                <>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Serviços (escolha um ou mais)</label>
                    <div className="flex flex-col gap-2">
                      {servicos.map(s => {
                        const marcado = servicosIds.includes(s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => alternarServico(s.id)}
                            className={`flex items-center justify-between gap-3 text-left border rounded-xl px-4 py-3 cursor-pointer transition-all ${marcado ? 'bg-[#F5F3FF] border-[#6D28D9]' : 'bg-[#F8F7FF] border-purple-50 hover:border-[#6D28D9]/30'}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${marcado ? 'bg-[#6D28D9] border-[#6D28D9]' : 'border-slate-300'}`}>
                                {marcado && <Check size={13} className="text-white" strokeWidth={3} />}
                              </span>
                              <span className="text-sm font-bold text-slate-800 truncate">{s.nome_servico}</span>
                            </div>
                            <span className="shrink-0 text-xs font-black text-[#6D28D9]">{formatarPreco(s.preco) || ''}</span>
                          </button>
                        );
                      })}
                    </div>
                    {servicos.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1.5"><AlertCircle size={14} /> Nenhum serviço cadastrado. Cadastre em Catálogo de Serviços primeiro.</p>
                    )}
                    {servicosIds.length > 0 && (
                      <div className="flex items-center justify-between gap-3 bg-[#F8F7FF] rounded-xl px-4 py-2.5 mt-1">
                        <span className="text-[11px] font-bold text-slate-500 flex items-center gap-1.5"><Clock size={12} /> {totalDuracao} min</span>
                        <span className="text-sm font-black text-slate-800 flex items-center gap-1.5"><DollarSign size={14} className="text-[#6D28D9]" /> {formatarPreco(totalPreco) || 'Grátis'}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Data</label>
                    <input
                      type="date"
                      min={hojeInput}
                      required
                      className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-medium text-slate-800 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                      value={formData.data}
                      onChange={e => setFormData({ ...formData, data: e.target.value })}
                    />
                  </div>

                  {servicosIds.length > 0 && formData.data && (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11px] font-black uppercase text-slate-500 tracking-wide ml-1">Horário</label>
                      {buscandoHorarios ? (
                        <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold py-3">
                          <Loader2 className="animate-spin" size={16} /> Buscando horários livres...
                        </div>
                      ) : horarios.length === 0 ? (
                        <p className="text-xs text-slate-400 italic py-2">Nenhum horário disponível nessa data. Tente outra data.</p>
                      ) : (
                        <div className="grid grid-cols-4 gap-2">
                          {horarios.map(h => (
                            <button
                              type="button"
                              key={h}
                              onClick={() => setFormData({ ...formData, horario: h })}
                              className={`py-2.5 rounded-xl text-xs font-black border cursor-pointer transition-all ${formData.horario === h ? 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-transparent shadow-sm' : 'bg-[#F8F7FF] text-slate-700 border-purple-50 hover:border-[#6D28D9]/30'}`}
                            >
                              {h}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex gap-3">
                  <input
                    type="date"
                    min={hojeInput}
                    required
                    className="flex-1 bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-medium text-slate-800 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                    value={formData.data}
                    onChange={e => setFormData({ ...formData, data: e.target.value })}
                  />
                  <input
                    type="time"
                    required
                    className="flex-1 bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-medium text-slate-800 outline-none transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                    value={formData.horario}
                    onChange={e => setFormData({ ...formData, horario: e.target.value })}
                  />
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <textarea
                  className="w-full bg-[#F8F7FF] border border-purple-50 py-3.5 px-4 rounded-xl text-sm font-medium text-slate-800 outline-none min-h-[90px] resize-none font-sans transition-all focus:bg-white focus:border-[#6D28D9] focus:ring-4 focus:ring-[#6D28D9]/10"
                  placeholder="Observações adicionais (opcional)"
                  value={formData.observacao}
                  onChange={e => setFormData({ ...formData, observacao: e.target.value })}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || (!editingConsultaId && (!formData.pacienteNome || servicosIds.length === 0 || !formData.horario))}
                className="w-full bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white border-none py-4 mt-2 rounded-xl text-[11px] font-black tracking-widest uppercase cursor-pointer flex items-center justify-center shadow-lg shadow-[#6D28D9]/20 hover:opacity-90 hover:-translate-y-0.5 active:scale-98 transition-all disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
              >
                {isSubmitting ? "PROCESSANDO..." : editingConsultaId ? "SALVAR ALTERAÇÕES" : "CONFIRMAR AGENDAMENTO"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualizarConsultas;